// ============================================================================
// EcgExplainPanel.tsx — блок «Почему»: доказательства из ответа сервиса разбора.
//
// Показывается ТОЛЬКО то, что реально пришло в result_json:
//   • находки с вероятностью, порогом и отклонением от порога;
//   • сведения модели (вероятности по классам, пороги, калибровка, допуск модели,
//     проверка сигнала перед моделью);
//   • отведения и длительность записи, на которой получен результат;
//   • числовые метрики качества снимка;
//   • какие измерения рассчитаны, а какие — нет.
//
// Если обоснования в ответе нет — явное «Нет объяснения», без домыслов.
// Вклад отведений/участков времени показывается только если сервис его
// вычислил (Integrated Gradients/окклюзия); иначе — причина, без выдуманных весов.
// ============================================================================

import type { EcgAnalysis } from '@/lib/ecgAnalysis';
import { ecgQualityVerdictLabel } from '@/lib/ecgLabels';
import {
    AttributionEvidence,
    ConfidenceEvidence,
    CriteriaEvidence,
    RationaleEvidence,
    SignalQualityEvidence,
} from '@/components/ecg/EcgExplainabilitySections';

const MODEL_STATUS_LABELS: Record<string, string> = {
    ok: 'применена',
    not_run: 'не запускалась',
    not_configured: 'не настроена',
    skipped: 'пропущена',
    error: 'ошибка',
};

const FINDING_SOURCE_LABELS: Record<string, string> = {
    model: 'модель',
    rr_heuristic: 'эвристика RR',
};

const MEASUREMENT_LABELS: Record<string, string> = {
    hr_bpm: 'ЧСС',
    pr_ms: 'PR',
    qrs_ms: 'QRS',
    qt_ms: 'QT',
    qtc_ms: 'QTc',
    axis_deg: 'Электрическая ось',
};

function fmtPercent(value: number | null): string {
    if (value === null || !Number.isFinite(value)) return '—';
    return `${Math.round(value * 100)}%`;
}

function fmtScore(value: number | null): string {
    if (value === null || !Number.isFinite(value)) return '—';
    return value.toFixed(2);
}

function fmtSeconds(value: number | null): string {
    if (value === null || !Number.isFinite(value)) return '—';
    return `${value.toFixed(1)} с`;
}

function fmtMs(value: number | null): string {
    if (value === null || !Number.isFinite(value)) return '—';
    return `${Math.round(value)} мс`;
}

/** Отклонение вероятности от порога в процентных пунктах (только при обоих значениях). */
function fmtThresholdDelta(probability: number | null, threshold: number | null): string {
    if (probability === null || threshold === null) return '';
    const delta = Math.round((probability - threshold) * 1000) / 10;
    if (!Number.isFinite(delta)) return '';
    return `${delta > 0 ? '+' : ''}${delta} п.п.`;
}

function Block({ title, children }: { title: string; children: React.ReactNode }) {
    return (
        <div className="space-y-1.5">
            <h4 className="text-[11px] uppercase tracking-wide text-muted-foreground">{title}</h4>
            {children}
        </div>
    );
}

function FindingCriteria({ result }: { result: EcgAnalysis }) {
    const findings = result.findings;
    const model = result.model;
    const hasModelProbabilities = !!model && Object.keys(model.probabilities).length > 0;

    if (findings.length === 0) {
        return (
            <Block title="Пороги находок">
                <div className="text-sm text-muted-foreground">
                    {hasModelProbabilities
                        ? 'Находок нет: ни одна вероятность не достигла порога — значения по классам ниже.'
                        : 'Находок нет; вероятности и пороги обнаружения сервис не передал.'}
                </div>
            </Block>
        );
    }

    return (
        <Block title="Пороги находок">
            <ul className="border rounded-md divide-y text-sm">
                {findings.map((finding, index) => (
                    <li key={`${finding.code}-${index}`} className="px-3 py-2 space-y-0.5">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                            <span className="font-medium">{finding.code}</span>
                            <span className="text-xs text-muted-foreground">
                                вероятность {fmtPercent(finding.probability)}
                            </span>
                        </div>
                        <div className="text-xs text-muted-foreground">
                            {finding.threshold !== null
                                ? `порог ${fmtPercent(finding.threshold)} · отклонение ${fmtThresholdDelta(finding.probability, finding.threshold) || '—'}`
                                : 'порог обнаружения не передан — отклонение от порога не рассчитано'}
                            {finding.probabilityRaw !== null && finding.probabilityRaw !== finding.probability && (
                                <span> · до калибровки {fmtPercent(finding.probabilityRaw)}</span>
                            )}
                            {finding.thresholdMargin !== null && (
                                <span> · запас {Math.round(finding.thresholdMargin * 1000) / 10} п.п.</span>
                            )}
                        </div>
                        <div className="flex flex-wrap gap-2 text-[11px] text-muted-foreground">
                            {finding.source && <span>источник: {FINDING_SOURCE_LABELS[finding.source] || finding.source}</span>}
                            {finding.calibrated === false && <span>вероятность не калибрована</span>}
                            {finding.calibrated === true && <span>вероятность калибрована</span>}
                        </div>
                    </li>
                ))}
            </ul>
        </Block>
    );
}

function ModelEvidence({ result }: { result: EcgAnalysis }) {
    const model = result.model;
    if (!model) {
        return (
            <Block title="Модель находок">
                <div className="text-sm text-muted-foreground">
                    Блок сведений о модели в ответе отсутствует — вероятности по классам и пороги недоступны.
                </div>
            </Block>
        );
    }

    const probabilities = Object.entries(model.probabilities).sort((left, right) => right[1] - left[1]);
    const gate = model.qualityGate;
    const signalGate = model.signalGate;
    const signalMetrics = signalGate.metrics;

    return (
        <Block title="Модель находок">
            <div className="text-sm">
                Модель {MODEL_STATUS_LABELS[model.status] || model.status || 'без статуса'}
                {model.reason && <span className="text-muted-foreground"> · {model.reason}</span>}
            </div>

            <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                {model.calibration && <span>калибровка: {model.calibration}</span>}
                {model.inferenceMs !== null && <span>вывод: {fmtMs(model.inferenceMs)}</span>}
                {model.checkpoint && (
                    <span>
                        чекпоинт: {model.checkpoint}
                        {model.sha256 && <span> · sha256 {model.sha256.slice(0, 12)}…</span>}
                    </span>
                )}
            </div>

            <div className="text-xs">
                Допуск модели по валидации:{' '}
                {gate.passed === null
                    ? 'не сообщён'
                    : gate.passed
                        ? 'пройден'
                        : `не пройден${gate.failed.length > 0 ? ` (${gate.failed.join(', ')})` : ''}`}
            </div>

            <div className="text-xs">
                Проверка сигнала перед моделью:{' '}
                {signalGate.ok === null ? 'не сообщена' : signalGate.ok ? 'пройдена' : 'не пройдена'}
                {signalGate.reasons.length > 0 && <span className="text-muted-foreground"> · {signalGate.reasons.join('; ')}</span>}
            </div>

            {signalGate.warnings.length > 0 && (
                <ul className="list-disc pl-5 text-xs text-muted-foreground space-y-0.5">
                    {signalGate.warnings.map((warning) => (
                        <li key={warning}>{warning}</li>
                    ))}
                </ul>
            )}

            {Object.keys(signalMetrics).length > 0 && (
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                    {signalMetrics.leads_valid !== undefined && (
                        <span>
                            пригодных отведений: {Math.round(signalMetrics.leads_valid)}
                            {signalMetrics.leads_expected !== undefined
                                ? ` из ${Math.round(signalMetrics.leads_expected)}`
                                : ''}
                        </span>
                    )}
                    {signalMetrics.duration_s !== undefined && <span>длительность: {fmtSeconds(signalMetrics.duration_s)}</span>}
                    {signalMetrics.fs_hz !== undefined && <span>частота: {Math.round(signalMetrics.fs_hz)} Гц</span>}
                    {signalMetrics.max_hf_ratio !== undefined && (
                        <span>высокочастотный шум: {signalMetrics.max_hf_ratio.toFixed(2)}</span>
                    )}
                </div>
            )}

            {probabilities.length === 0 ? (
                <div className="text-sm text-muted-foreground">
                    Вероятностей по классам нет — модель не передала их в ответе.
                </div>
            ) : (
                <div className="border rounded-md overflow-x-auto">
                    <table className="w-full text-xs">
                        <thead className="bg-muted/40 text-muted-foreground">
                            <tr>
                                <th className="text-left font-medium px-2 py-1">Класс</th>
                                <th className="text-right font-medium px-2 py-1">Вероятность</th>
                                <th className="text-right font-medium px-2 py-1">Порог</th>
                                <th className="text-right font-medium px-2 py-1">Отношение к порогу</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y">
                            {probabilities.map(([code, probability]) => {
                                const threshold = model.thresholds[code];
                                const hasThreshold = typeof threshold === 'number' && Number.isFinite(threshold);
                                return (
                                    <tr key={code}>
                                        <td className="px-2 py-1">{code}</td>
                                        <td className="px-2 py-1 text-right">{fmtPercent(probability)}</td>
                                        <td className="px-2 py-1 text-right">{hasThreshold ? fmtPercent(threshold) : '—'}</td>
                                        <td className="px-2 py-1 text-right text-muted-foreground">
                                            {hasThreshold
                                                ? probability >= threshold
                                                    ? `превышает (${fmtThresholdDelta(probability, threshold)})`
                                                    : 'ниже порога'
                                                : 'порог не передан'}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </Block>
    );
}

function SignalEvidence({ result }: { result: EcgAnalysis }) {
    const signal = result.signal;
    if (signal.leads.length === 0) {
        return (
            <Block title="Отведения и время">
                <div className="text-sm text-muted-foreground">
                    Список прослеженных отведений не передан — сигнал не извлекался.
                </div>
            </Block>
        );
    }

    return (
        <Block title="Отведения и время">
            <div className="text-sm">
                Отведений: {signal.leads.length} · длительность записи {fmtSeconds(signal.durationS)}
                {signal.fsHz !== null && <span className="text-muted-foreground"> · {Math.round(signal.fsHz)} Гц</span>}
            </div>
            <div className="flex flex-wrap gap-1">
                {signal.leads.map((lead) => (
                    <span
                        key={lead.name}
                        className="text-[11px] border rounded px-1.5 py-0.5 bg-background"
                        title={lead.durationS !== null ? `длительность ${fmtSeconds(lead.durationS)}` : undefined}
                    >
                        {lead.name}
                    </span>
                ))}
            </div>
            <div className="text-[11px] text-muted-foreground">
                Модель получает весь прослеженный сигнал; вклад отдельных отведений и участков времени сервис не передаёт.
            </div>
        </Block>
    );
}

function QualityEvidence({ result }: { result: EcgAnalysis }) {
    const quality = result.quality;
    const hasMetrics = quality.sharpness !== null || quality.exposure !== null || quality.coverage !== null;
    if (!hasMetrics) {
        return (
            <Block title="Качество снимка">
                <div className="text-sm text-muted-foreground">
                    Числовых метрик качества нет (передан только вердикт «{ecgQualityVerdictLabel(quality.verdict)}»).
                </div>
            </Block>
        );
    }

    return (
        <Block title="Качество снимка">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                <div>вердикт: {ecgQualityVerdictLabel(quality.verdict)}</div>
                <div>резкость: {fmtScore(quality.sharpness)}</div>
                <div>экспозиция: {fmtScore(quality.exposure)}</div>
                <div>покрытие листа: {fmtScore(quality.coverage)}</div>
                <div>
                    калибровка:{' '}
                    {quality.calibrationFound === null ? '—' : quality.calibrationFound ? 'найдена' : 'не найдена'}
                </div>
                <div>px/мм: {quality.pxPerMm !== null ? quality.pxPerMm.toFixed(2) : '—'}</div>
                <div>отведений: {quality.leadsExtracted !== null ? Math.round(quality.leadsExtracted) : '—'}</div>
            </div>
        </Block>
    );
}

function MeasurementsEvidence({ result }: { result: EcgAnalysis }) {
    const availability = Object.entries(result.measurements.availability);
    if (availability.length === 0) {
        return (
            <Block title="Измерения">
                <div className="text-sm text-muted-foreground">
                    Сервис не сообщил, какие измерения рассчитаны.
                </div>
            </Block>
        );
    }

    const calculated = availability.filter(([, value]) => value).map(([key]) => MEASUREMENT_LABELS[key] || key);
    const missing = availability.filter(([, value]) => !value).map(([key]) => MEASUREMENT_LABELS[key] || key);

    return (
        <Block title="Измерения">
            <div className="text-sm">Рассчитано: {calculated.length > 0 ? calculated.join(', ') : '—'}</div>
            {missing.length > 0 && (
                <div className="text-sm text-muted-foreground">Не рассчитано: {missing.join(', ')}</div>
            )}
            <div className="text-[11px] text-muted-foreground">
                {result.explainability
                    ? 'Пороговые отклонения — в блоке «Критерии и пороги».'
                    : 'Пороговые нормы для измерений в ответе отсутствуют — сравнение с нормой не выполняется.'}
            </div>
        </Block>
    );
}

function NoEvidence({ result }: { result: EcgAnalysis }) {
    const model = result.model;
    const modelNote = model
        ? ` Модель находок: ${MODEL_STATUS_LABELS[model.status] || model.status || 'без статуса'}${model.reason ? ` — ${model.reason}` : ''}.`
        : '';
    return (
        <div className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 space-y-1">
            <div className="text-sm font-medium">Нет объяснения</div>
            <div className="text-xs">
                Сервис разбора не передал обоснование: нет критериального слоя, порогов и вероятностей, сведений о модели, отведениях и метриках качества.
                {modelNote}
                {result.reason ? ` Причина разбора: ${result.reason}` : ''}
            </div>
        </div>
    );
}

export function EcgExplainPanel({ result }: { result: EcgAnalysis }) {
    // Объяснением считается ЛЮБОЙ факт из ответа: находки, вероятности/пороги
    // модели, отведения, числовые метрики качества, сведения об измерениях.
    // Один лишь блок «model: not_run» (без данных) — ещё не объяснение.
    const model = result.model;
    const hasExplanation =
        result.findings.length > 0 ||
        Object.keys(model?.probabilities ?? {}).length > 0 ||
        Object.keys(model?.thresholds ?? {}).length > 0 ||
        result.signal.leads.length > 0 ||
        result.quality.sharpness !== null ||
        result.quality.exposure !== null ||
        result.quality.coverage !== null ||
        Object.keys(result.measurements.availability).length > 0 ||
        result.explainability !== null;

    return (
        <section className="border rounded-lg bg-card">
            <div className="px-3 py-2 border-b">
                <h3 className="text-xs uppercase tracking-wide text-muted-foreground">Почему</h3>
                <div className="text-[11px] text-muted-foreground mt-0.5">
                    Только данные ответа сервиса; предположения не добавляются.
                </div>
            </div>
            <div className="p-3 space-y-3">
                {result.status !== 'ok' && (
                    <div className="text-xs text-muted-foreground">
                        Разбор не завершён (статус «{result.status || 'не указан'}») — показано только то, что передал сервис.
                    </div>
                )}
                {!hasExplanation ? (
                    <NoEvidence result={result} />
                ) : (
                    <>
                        {result.explainability && (
                            <RationaleEvidence rationale={result.explainability.rationale} />
                        )}
                        {result.explainability && (
                            <AttributionEvidence attribution={result.explainability.attribution} />
                        )}
                        {result.explainability && (
                            <CriteriaEvidence explainability={result.explainability} />
                        )}
                        {result.explainability && (
                            <ConfidenceEvidence confidence={result.explainability.confidence} />
                        )}
                        <FindingCriteria result={result} />
                        <ModelEvidence result={result} />
                        {result.explainability ? (
                            <SignalQualityEvidence explainability={result.explainability} />
                        ) : (
                            <SignalEvidence result={result} />
                        )}
                        <QualityEvidence result={result} />
                        <MeasurementsEvidence result={result} />
                    </>
                )}
            </div>
        </section>
    );
}
